import { useCallback, useEffect, useRef, useState } from 'react'

import type { AiRankedCandidate } from '@/features/recruiter/api/candidates.api'
import { getEcho } from '@/lib/echo'
import { useAuthStore } from '@/store/authStore'

interface ChunkScoredPayload {
  searchRunId: string
  results: AiRankedCandidate[]
}

interface CompletedPayload {
  searchRunId: string
}

/**
 * Listens on the same private user channel notifications already use for
 * two custom broadcast events (AiCandidateSearchChunkScored/Completed) and
 * accumulates results as they stream in from ScoreCandidateSearchChunkJob,
 * instead of waiting for one long synchronous AI call to finish.
 */
export function useAiCandidateSearchStream() {
  const userId = useAuthStore((s) => s.user?.id)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [results, setResults] = useState<AiRankedCandidate[]>([])
  const [isComplete, setIsComplete] = useState(false)
  const [totalExpected, setTotalExpected] = useState(0)
  // Mirrors activeRunId for the Echo listener closures below, which are
  // registered once per userId and would otherwise compare against a
  // stale value from whichever run was active when they were attached.
  const activeRunIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!userId) return

    const echo = getEcho()
    const channel = echo.private(`App.Models.User.${userId}`)

    const onChunkScored = (payload: ChunkScoredPayload) => {
      if (payload.searchRunId !== activeRunIdRef.current) return
      setResults((prev) => [...prev, ...payload.results].sort((a, b) => b.aiScore - a.aiScore))
    }

    const onCompleted = (payload: CompletedPayload) => {
      if (payload.searchRunId !== activeRunIdRef.current) return
      setIsComplete(true)
    }

    channel.listen('.ai-candidate-search.chunk-scored', onChunkScored)
    channel.listen('.ai-candidate-search.completed', onCompleted)

    return () => {
      channel.stopListening('.ai-candidate-search.chunk-scored', onChunkScored)
      channel.stopListening('.ai-candidate-search.completed', onCompleted)
    }
  }, [userId])

  const startRun = useCallback((searchRunId: string, filteredCount: number) => {
    activeRunIdRef.current = searchRunId
    setActiveRunId(searchRunId)
    setResults([])
    setIsComplete(filteredCount === 0)
    setTotalExpected(filteredCount)
  }, [])

  const clearRun = useCallback(() => {
    activeRunIdRef.current = null
    setActiveRunId(null)
    setResults([])
    setIsComplete(false)
    setTotalExpected(0)
  }, [])

  return {
    isActive: activeRunId !== null,
    results,
    isComplete,
    totalExpected,
    startRun,
    clearRun,
  }
}
